import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Auth() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [form, setForm] = useState({ name: "", email: "", password: "", token: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
        nav(u.role === "admin" ? "/admin" : "/account");
      } else if (mode === "register") {
        const u = await register(form.name, form.email, form.password);
        toast.success("Welcome to Crystal Clear Craft");
        nav("/account");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot", { email: form.email });
        toast.success("If we found your account, a reset token is on its way. (Check console logs — email mocked in dev)");
        setMode("reset");
      } else if (mode === "reset") {
        await api.post("/auth/reset", { token: form.token, password: form.password });
        toast.success("Password updated. Please sign in.");
        setMode("login");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Please try again");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1615529182904-14819c35db37" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-charcoal/40" />
        <div className="absolute bottom-16 left-16 text-ivory max-w-md">
          <div className="overline text-gold mb-4">The house account</div>
          <div className="font-serif text-4xl leading-tight">Save favourites, track orders, and be first to hold the newest pieces.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md">
          <div className="overline text-emerald mb-3">{mode === "login" ? "Welcome back" : mode === "register" ? "New here?" : mode === "forgot" ? "Reset" : "Set new password"}</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-8">
            {mode === "login" && "Sign in."}
            {mode === "register" && "Create account."}
            {mode === "forgot" && "Forgot password?"}
            {mode === "reset" && "New password."}
          </h1>

          <div className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="overline text-charcoal/60 mb-2 block">Full name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" className="bg-transparent border-0 border-b divider-gold rounded-none focus-visible:ring-0 py-3" />
              </div>
            )}
            {mode !== "reset" && (
              <div>
                <label className="overline text-charcoal/60 mb-2 block">Email</label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" className="bg-transparent border-0 border-b divider-gold rounded-none focus-visible:ring-0 py-3" />
              </div>
            )}
            {mode === "reset" && (
              <div>
                <label className="overline text-charcoal/60 mb-2 block">Reset token</label>
                <Input required value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} data-testid="auth-token" className="bg-transparent border-0 border-b divider-gold rounded-none focus-visible:ring-0 py-3" />
              </div>
            )}
            {(mode === "login" || mode === "register" || mode === "reset") && (
              <div>
                <label className="overline text-charcoal/60 mb-2 block">Password</label>
                <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" className="bg-transparent border-0 border-b divider-gold rounded-none focus-visible:ring-0 py-3" />
              </div>
            )}
          </div>

          <Button data-testid="auth-submit" disabled={loading} type="submit" className="w-full mt-8 rounded-none bg-emerald hover:bg-emerald/90 text-ivory h-12 tracking-[0.15em] uppercase text-xs">
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset token" : "Update password"}
          </Button>

          <div className="mt-8 text-xs text-charcoal/60 flex flex-wrap items-center justify-center gap-4">
            {mode === "login" && (<>
              <button type="button" data-testid="switch-register" onClick={() => setMode("register")} className="hover:text-emerald">Create an account</button>
              <button type="button" data-testid="switch-forgot" onClick={() => setMode("forgot")} className="hover:text-emerald">Forgot password?</button>
            </>)}
            {mode === "register" && (
              <button type="button" data-testid="switch-login" onClick={() => setMode("login")} className="hover:text-emerald">Have an account? Sign in</button>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <button type="button" onClick={() => setMode("login")} className="hover:text-emerald">Back to sign in</button>
            )}
          </div>
          <p className="mt-6 text-center text-[11px] text-charcoal/40">
            Admin? Use <span className="font-mono">admin@crystalclearcraft.com</span> / <span className="font-mono">Admin@123</span>
          </p>
        </form>
      </div>
    </div>
  );
}
